import React from 'react';
import { AlertTriangle, RotateCw, Home } from 'lucide-react';

import Logo from './Logo';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Top-level error boundary.
 *
 * React unmounts the entire tree when a component throws during render, which
 * leaves the user staring at a blank page. This catches that and shows a
 * recoverable screen instead.
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const isDev = import.meta.env.VITE_ENV === 'development';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-cosmic-950 px-4 py-10">
        <div className="absolute inset-0 bg-gradient-to-b from-cosmic-950 via-cosmic-900 to-cosmic-950" />

        <div className="relative w-full max-w-md">
          {/* isLink={false} is required: this renders outside <BrowserRouter>,
              and Logo's default <Link> would throw without router context. */}
          <div className="mb-7 flex justify-center">
            <Logo
              isLink={false}
              imageSize="w-12 h-12"
              textSize="text-2xl"
              showSubheading
            />
          </div>

          <div className="rounded-2xl border border-white/20 bg-white/5 p-8 text-center backdrop-blur-sm">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-red-500/30 bg-red-500/10">
              <AlertTriangle className="h-7 w-7 text-red-400" />
            </div>

            <h1 className="mb-2 font-serif text-2xl font-bold text-white">
              Something went wrong
            </h1>
            <p className="mb-7 text-sm text-purple-200">
              The page ran into an unexpected problem. Reloading usually fixes it.
            </p>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                onClick={this.handleReload}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-600 to-secondary-600 py-3 text-sm font-bold text-white transition-colors hover:from-primary-700 hover:to-secondary-700 active:scale-[0.98]"
              >
                <RotateCw className="h-4 w-4" />
                Reload Page
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 py-3 text-sm font-bold text-purple-100 transition-colors hover:bg-white/10 active:scale-[0.98]"
              >
                <Home className="h-4 w-4" />
                Go Home
              </button>
            </div>

            {isDev && this.state.error && (
              <pre className="mt-6 max-h-56 overflow-auto whitespace-pre-wrap break-words rounded-xl border border-white/10 bg-cosmic-950/60 p-3 text-left text-xs text-red-300">
                {this.state.error.stack || this.state.error.message}
              </pre>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
